import { useModal } from 'components/modals';
import { CloseModal } from 'components/modals/types';
import { PlacidTemplate } from 'features/design-templates/types';
import { Dispatch, SetStateAction, useEffect, useRef } from 'react';
import styled from 'styled-components';

// Import LayerConfiguration type
type LayerConfiguration = Record<string, unknown>;

type PlacidEditorModalProps = {
  accessToken: string;
  templateId: string;
  prefilledLayers: LayerConfiguration | undefined;
  onClose?: () => void;
  setTemplates: Dispatch<SetStateAction<PlacidTemplate[] | null>>;
};

type Props = CloseModal & PlacidEditorModalProps;

export const PlacidEditorModal = ({
  closeModal,
  accessToken,
  templateId,
  prefilledLayers,
  onClose,
  setTemplates
}: Props) => {
  const editorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!editorRef.current) {
      return;
    }

    // eslint-disable-next-line no-console
    console.log('open editor with ', {
      access_token: accessToken,
      template_uuid: templateId,
      prefill_layers: prefilledLayers
    });

    const editor = window.EditorSDK?.editor.create(editorRef.current, {
      access_token: accessToken,
      template_uuid: templateId,
      prefill_layers_by_schema: prefilledLayers
    });

    editor?.on('editor:closed', () => {
      onClose?.();
      closeModal();
    });
    editor?.on('editor:template:saved', () => {
      setTemplates(templates => {
        return (
          templates?.map(t => {
            if (t.uuid === templateId) {
              return { ...t, isPrefilled: true } as PlacidTemplate;
            }
            return t as PlacidTemplate;
          }) || null
        );
      });
    });

    return () => {
      editor?.destroy();
    };
  }, [accessToken, closeModal, onClose, prefilledLayers, templateId, setTemplates]);

  return (
    <EditorContainer>
      <EditorWrapper ref={editorRef} />
    </EditorContainer>
  );
};

export const useShowPlacidEditorModal = () => {
  const { showModal } = useModal();

  return (props: PlacidEditorModalProps) => {
    showModal('PLACID_EDITOR', {
      ...props,
      size: 'fullscreen'
    });
  };
};

const EditorContainer = styled.div`
  width: 100vw;
  height: 100vh;
  display: flex;
  flex-direction: column;
`;

const EditorWrapper = styled.div`
  flex: 1;
`;
